import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Google "G" logo as inline SVG
function GoogleLogo({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export function GoogleReviews() {
  const [reviews, setReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch(`${API_URL}/api/reviews?min_rating=5`);
        const data = await res.json();
        setReviews(data.reviews || []);
        setOverallRating(data.overall_rating || 0);
        setTotalReviews(data.total_reviews || 0);
      } catch {
        // Silently fail — section just won't show
      } finally {
        setLoading(false);
      }
    }
    fetchReviews();
  }, []);

  const nextReview = () => setActiveIndex((prev) => (prev + 1) % reviews.length);
  const prevReview = () => setActiveIndex((prev) => (prev - 1 + reviews.length) % reviews.length);

  if (loading || reviews.length === 0) return null;

  return (
    <section id="google-reviews" className="py-20 bg-[#FDF8F4] relative" data-testid="google-reviews-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <GoogleLogo className="w-8 h-8" />
            <span className="font-manrope text-sm font-semibold text-[#555] uppercase tracking-wider">Google Reviews</span>
          </div>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-3" data-testid="google-reviews-heading">
            What Our Guests Say
          </h2>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${i < Math.round(overallRating) ? 'fill-[#FBBC05] text-[#FBBC05]' : 'fill-gray-200 text-gray-200'}`}
                />
              ))}
            </div>
            <span className="font-manrope text-lg font-bold text-[#2C2C2C]">{overallRating}</span>
            <span className="font-manrope text-sm text-[#888]">({totalReviews} reviews)</span>
          </div>
        </motion.div>

        {/* Review Card Carousel */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm border border-[#e8e0d4] relative"
              data-testid={`google-review-card-${activeIndex}`}
            >
              {/* Google badge */}
              <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
                <a
                  href={reviews[activeIndex]?.google_maps_uri || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 bg-[#f8f8f8] hover:bg-[#f0f0f0] px-3 py-1.5 rounded-full transition-colors"
                  data-testid="google-review-link"
                >
                  <GoogleLogo className="w-4 h-4" />
                  <span className="font-manrope text-xs text-[#555] font-medium">Google</span>
                  <ExternalLink className="w-3 h-3 text-[#888]" />
                </a>
              </div>

              {/* Stars */}
              <div className="flex gap-0.5 mb-5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
                ))}
              </div>

              {/* Review Text */}
              <p className="font-manrope text-[#444] text-base sm:text-lg leading-relaxed mb-6 pr-8 sm:pr-0" data-testid="google-review-text">
                "{reviews[activeIndex]?.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                {reviews[activeIndex]?.profile_photo_url ? (
                  <img
                    src={reviews[activeIndex].profile_photo_url}
                    alt={reviews[activeIndex].author_name}
                    className="w-11 h-11 rounded-full object-cover border-2 border-[#e8e0d4]"
                    data-testid="google-review-avatar"
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#6B1C23] flex items-center justify-center text-white font-bold text-lg">
                    {reviews[activeIndex]?.author_name?.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-manrope font-semibold text-[#2C2C2C] text-sm" data-testid="google-review-author">
                    {reviews[activeIndex]?.author_name}
                  </p>
                  <p className="font-manrope text-xs text-[#999]">{reviews[activeIndex]?.relative_time}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {reviews.length > 1 && (
            <>
              <button
                onClick={prevReview}
                className="absolute left-2 sm:left-0 top-1/2 -translate-y-1/2 sm:-translate-x-5 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-[#e8e0d4] shadow-sm flex items-center justify-center hover:bg-[#faf7f2] transition-colors z-10"
                data-testid="review-prev-btn"
              >
                <ChevronLeft className="w-5 h-5 text-[#2C2C2C]" />
              </button>
              <button
                onClick={nextReview}
                className="absolute right-2 sm:right-0 top-1/2 -translate-y-1/2 sm:translate-x-5 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-[#e8e0d4] shadow-sm flex items-center justify-center hover:bg-[#faf7f2] transition-colors z-10"
                data-testid="review-next-btn"
              >
                <ChevronRight className="w-5 h-5 text-[#2C2C2C]" />
              </button>
            </>
          )}
        </div>

        {/* Dots */}
        {reviews.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === activeIndex ? 'bg-[#6B1C23] w-6' : 'bg-[#d4c8b8] hover:bg-[#b0a090]'
                }`}
                data-testid={`review-dot-${i}`}
              />
            ))}
          </div>
        )}

        {/* Review Us + View All */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <motion.a
            href="https://g.page/r/CbKi5__xhDr7EAE/review"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.04, boxShadow: '0 8px 30px rgba(107,28,35,0.2)' }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2.5 bg-[#6B1C23] text-white px-8 py-3.5 rounded-full font-manrope font-semibold text-sm shadow-md hover:bg-[#8B2430] transition-colors"
            data-testid="review-us-btn"
          >
            <Star className="w-4.5 h-4.5 fill-[#FBBC05] text-[#FBBC05]" />
            Leave Us a Review
            <GoogleLogo className="w-4 h-4" />
          </motion.a>

          <a
            href="https://www.google.com/maps/place/Salam+Grill/@32.9505,-96.7128,17z/data=!4m8!3m7!1s0x864c1ffa1b3763c1:0xfb3a84f1ffe7a2b2!8m2!3d32.9505!4d-96.7128!9m1!1b1!16s%2Fg%2F11wlpc8p07"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-manrope text-sm text-[#6B1C23] hover:text-[#8B2430] font-semibold transition-colors"
            data-testid="view-all-google-reviews"
          >
            <GoogleLogo className="w-4 h-4" />
            View All {totalReviews} Reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
